import { useCallback, useMemo, useState } from "react"
import { GetApp } from "../../App"
import { CruiseContext, GetShip } from "../../App/Context"
import { useRefCallbacks } from "../../App/useRefCallbacks"
import { classNames } from "../../Components"
import { Loading } from "../../Components/Loading"
import { Menu } from "../../Components/Menu"
import type { TabBarCallbacks } from "../../Components/TabBar"
import { PrintMode } from "../PrintView"
import { GetShipView, ShipRoutes } from "../ShipView"
import { ShipInfoHeaderView } from "../ShipInfoHeaderView"
import { CruiseContentView } from "./ContentView"
import { useCruiseData } from "./DataContext"
import { CruiseDetailsView } from "./DetailsView"
import type { CruiseDataProps } from "./Params"
import { CruiseTitleView } from "./TitleView"

export function CruiseViewUI({
  cruiseChildren,
  cruiseId,
  focus,
  initDetails,
  initTab,
}: CruiseDataProps) {
  const app = GetApp();
  const ship = GetShip();
  const { navigateShip, setPrinting } = GetShipView();
  const { cruise, tracks, trips } = useCruiseData();
  const t = app.getT("CruiseView");
  const [details, setDetails] = useState<boolean>(initDetails || false);
  const [showLabels, setShowLabels] = useState<boolean>(false);
  const tabBarRef = useRefCallbacks<TabBarCallbacks>();
  const currentCruise = app.current?.cruiseId === cruiseId;

  const actions = useMemo(() => [], [cruise, ship, trips, app.current]);
  const shipHeader = useMemo(() => {
    const ShipHeader = () => <></>;
    return ShipHeader;
  }, [cruise, details, ship, trips]);

  const toggleLabels = useCallback(() => {
    setShowLabels((current) => !current);
  }, []);

  const menus = useMemo(() => {
    if (!app.hasFeature("labels")) {
      return [];
    }
    return [
      <Menu
        key="labels"
        button={t("menu_labels")}
        buttonClass="button"
        global={true}
        items={[
          {
            id: "toggle-labels",
            onClick: toggleLabels,
            title: t(`menu_toggle_labels_${showLabels ? "on" : "off"}`),
          },
        ]}
      />,
    ];
  }, [app, showLabels, t, toggleLabels]);

  if (!cruise) {
    return <Loading />;
  }

  const state = { cruise, showLabels, trips: trips || [] };
  return (
    <CruiseContext.Provider value={state}>
      {!cruiseChildren && (
        <ShipInfoHeaderView actions={actions} customMenus={menus} shipHeader={shipHeader} />
      )}
      <div className={classNames(["CruiseView", currentCruise && "current", focus && "focus"])}>
        <CruiseTitleView
          cruise={cruise}
          onDetailsChange={setDetails}
          onClose={() => navigateShip(ShipRoutes.calendar)}
        />
        {details && (
          <CruiseDetailsView
            cruise={cruise}
            tracks={tracks}
            initTab={initTab}
            tabBarRef={tabBarRef}
            onPrint={() => setPrinting(PrintMode.cruise, cruise)}
          />
        )}
        {cruiseChildren
          ? cruiseChildren
          : trips
            ? <CruiseContentView trips={trips} />
            : <Loading />}
      </div>
    </CruiseContext.Provider>
  );
}
